import { apiRequest } from './axiosConfig';

export type PartnerStatus = 'active' | 'suspended';

export type AllowedTrialDay = 7 | 14 | 30;

// 有料転換ボーナスの種別: なし / プラン1ヶ月分 / 固定クレジット
export type ConversionBonusType = 'none' | 'plan_month' | 'fixed';

// ポータル（DynamoDB）が正本のパートナー企業マスタ。Tasky 側は admin プロキシ経由で操作する。
export interface PortalPartner {
  id: string;
  name: string;
  brand?: string | null;
  code: string;
  status: PartnerStatus;
  defaultCommissionRate?: number | null;
  defaultWholesaleRate?: number | null;
  incentiveNote?: string | null;
  allowedTrialDays?: AllowedTrialDay[] | null;
  conversionBonusCredits?: number | null;
  conversionBonusType?: ConversionBonusType | null;
  billingEmails?: string[] | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface PartnerCreateRequest {
  name: string;
  code: string;
  brand?: string | null;
  defaultCommissionRate?: number | null;
  defaultWholesaleRate?: number | null;
  allowedTrialDays?: AllowedTrialDay[] | null;
  conversionBonusCredits?: number | null;
  conversionBonusType?: ConversionBonusType | null;
  incentiveNote?: string | null;
  billingEmails?: string[] | null;
}

export interface PartnerUpdateRequest extends Partial<PartnerCreateRequest> {
  status?: PartnerStatus;
}

// Tasky DB 上の組織のパートナー帰属情報。
export interface AttributedOrganization {
  id: number;
  name: string;
  partner_ref: string | null;
  external_ref: string | null;
  plan: string | null;
  trial_ends_at: string | null;
  created_at: string | null;
  leader_email?: string | null;
}

// パートナー経由で入ってきた組織。帰属の記録先が2本あるため source で経路を示す。
export type PartnerReferralSource = 'invite_url' | 'portal_provision';

export type PartnerReferralStatus = 'active' | 'trial' | 'trial_expired' | 'suspended' | 'churned';

export interface PartnerReferral {
  id: number;
  name: string;
  partner_ref: string | null;
  portal_partner_id: string | null;
  external_ref: string | null;
  partner_name: string | null;
  partner_status: PartnerStatus | null;
  source: PartnerReferralSource;
  plan: string | null;
  status: PartnerReferralStatus;
  paid_active: boolean;
  trial_ends_at: string | null;
  created_at: string | null;
  leader_name: string | null;
  leader_email: string | null;
}

export interface PartnerReferralsResult {
  organizations: PartnerReferral[];
  total: number;
  /** ポータルの企業マスタを引けたか。false のときは企業名が null になる。 */
  portal_directory_available: boolean;
}

// 手数料売上（代理店ごと・顧客ごと × 月次）。集計はポータル側が正本。
export type CommissionDirection = 'to_richika' | 'from_richika';

export interface CommissionCell {
  amount: number;
  estimated: boolean;
  clientCount?: number;
  unitPrice?: number | null;
  rate?: number | null;
}

export interface CommissionPartnerRow {
  partnerId: string;
  partnerName: string | null;
  partnerCode: string | null;
  monthly: Record<string, CommissionCell>;
  total: number;
}

export interface CommissionClientRow extends Omit<CommissionPartnerRow, 'partnerId'> {
  clientId: string;
  companyName: string;
  partnerId: string;
}

export interface CommissionSection {
  direction: CommissionDirection;
  label: string;
  totals: Record<string, CommissionCell>;
  grandTotal: number;
  /** 確定ではなく見込みで埋めた月（当月）。 */
  estimatedMonths: string[];
  byPartner: CommissionPartnerRow[];
  byClient: CommissionClientRow[];
}

export interface CommissionReport {
  months: string[];
  sections: CommissionSection[];
}

interface PartnersResponse {
  partners: PortalPartner[];
}

interface OrganizationsResponse {
  organizations: AttributedOrganization[];
}

const adminPartnerPortalService = {
  async getPartners(): Promise<PortalPartner[]> {
    const response = await apiRequest.get<PartnersResponse>('admin/partner-portal/partners');
    return response.partners ?? [];
  },

  async createPartner(data: PartnerCreateRequest): Promise<void> {
    await apiRequest.post('admin/partner-portal/partners', data);
  },

  async updatePartner(id: string, data: PartnerUpdateRequest): Promise<void> {
    await apiRequest.patch(`admin/partner-portal/partners/${encodeURIComponent(id)}`, data);
  },

  async deletePartner(id: string): Promise<void> {
    await apiRequest.delete(`admin/partner-portal/partners/${encodeURIComponent(id)}`);
  },

  async getReferrals(params: { partner?: string; q?: string } = {}): Promise<PartnerReferralsResult> {
    const response = await apiRequest.get<PartnerReferralsResult>('admin/partner-portal/referrals', { params });
    return {
      organizations: response.organizations ?? [],
      total: response.total ?? 0,
      portal_directory_available: response.portal_directory_available ?? true,
    };
  },

  async getCommissions(params: { months?: number } = {}): Promise<CommissionReport> {
    const response = await apiRequest.get<CommissionReport>('admin/partner-portal/commissions', { params });
    return { months: response.months ?? [], sections: response.sections ?? [] };
  },

  async searchOrganizations(params: { partner_ref?: string; q?: string }): Promise<AttributedOrganization[]> {
    const response = await apiRequest.get<OrganizationsResponse>('admin/partner-portal/organizations', {
      params,
    });
    return response.organizations ?? [];
  },

  async updateOrganizationAttribution(id: number, partnerRef: string | null): Promise<void> {
    await apiRequest.patch(`admin/partner-portal/organizations/${id}`, { partner_ref: partnerRef });
  },
};

export default adminPartnerPortalService;
